import React, { useState } from 'react';
import { AlertTriangle, Loader2, LogOut, ShieldOff, Trash2 } from 'lucide-react';
import { deactivateOwnAccount, requestAccountDeletion } from '../../App/Services/UserService';
import { storage } from '../Utils/authStorage';

type Modal = 'deactivate' | 'delete' | null;

const REASON_LIMIT = 1000;

export interface AccountAccessPanelProps {
  /** Classes for the surrounding block, so each screen can match its own card styling. */
  className?: string;
  /** Rule-specific note shown above the actions, e.g. the company-admin precondition. */
  hint?: React.ReactNode;
}

/**
 * Self-service account access controls: switch the account off, or ask for permanent
 * deletion.
 *
 * <p>Permanent removal is deliberately not offered here. Deactivation is immediate and
 * reversible; deletion is a request that a SUPER_ADMIN reviews and either runs or refuses,
 * so an irreversible step always has an identified approver behind it and always carries
 * the user's stated reason.</p>
 *
 * <p>Both endpoints derive the caller from the JWT, so nothing here names an account —
 * they can only ever act on the person holding the button. The backend owns the rules and
 * answers with a plain message when an account is protected (the last super admin, or the
 * only active company admin in an organisation), which is surfaced inside the modal next
 * to the action that failed rather than as a toast that could be missed.</p>
 */
const AccountAccessPanel: React.FC<AccountAccessPanelProps> = ({
  className = '',
  hint,
}) => {
  const [modal, setModal] = useState<Modal>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const closeModal = () => {
    if (busy) return;
    setModal(null);
    setReason('');
    setError('');
  };

  const signOut = () => {
    storage.clearAuth();
    window.location.href = '/login';
  };

  const isDeleteModal = modal === 'delete';
  const canSubmit = isDeleteModal
    ? reason.trim().length > 0 && reason.trim().length <= REASON_LIMIT
    : reason.trim().length <= REASON_LIMIT;

  const submit = async () => {
    setBusy(true);
    setError('');

    try {
      const response = isDeleteModal
        ? await requestAccountDeletion(reason)
        : await deactivateOwnAccount(reason);

      if (response.success) {
        signOut();
        return;
      }

      setError(response.message || 'Could not update your account.');
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={className}>
      {hint && <p className="mb-3 text-xs font-semibold text-slate-500">{hint}</p>}

      <div className="flex flex-wrap gap-3">
        <button
          type="button"
          onClick={() => setModal('deactivate')}
          className="flex items-center justify-center gap-2 rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-2.5 text-xs font-black uppercase tracking-widest text-amber-400 transition hover:bg-amber-500/20 hover:text-amber-300"
        >
          <ShieldOff size={15} />
          Deactivate account
        </button>

        <button
          type="button"
          onClick={() => setModal('delete')}
          className="flex items-center justify-center gap-2 rounded-xl border border-rose-500/40 bg-rose-500/10 px-4 py-2.5 text-xs font-black uppercase tracking-widest text-rose-400 transition hover:bg-rose-500/20 hover:text-rose-300"
        >
          <Trash2 size={15} />
          Request deletion
        </button>
      </div>

      <p className="mt-3 text-xs leading-relaxed text-slate-500">
        Deactivating switches your access off and you can reactivate it yourself at sign-in.
        Permanent deletion is reviewed by an administrator first — we will email you either way.
      </p>

      {modal && (
        <div
          className="fixed inset-0 z-[110] flex items-center justify-center bg-slate-950/80 p-4 backdrop-blur-sm"
          onClick={closeModal}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-label={isDeleteModal ? 'Request account deletion' : 'Deactivate account'}
            onClick={(e) => e.stopPropagation()}
            className={`w-full max-w-md rounded-2xl border bg-slate-900 p-6 shadow-2xl ${
              isDeleteModal ? 'border-rose-500/30' : 'border-amber-500/30'
            }`}
          >
            <div className="flex items-start gap-4">
              <span
                className={`flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl border ${
                  isDeleteModal
                    ? 'border-rose-500/30 bg-rose-500/10 text-rose-400'
                    : 'border-amber-500/30 bg-amber-500/10 text-amber-400'
                }`}
              >
                <AlertTriangle size={20} />
              </span>
              <div>
                <h3 className="text-base font-black tracking-tight text-slate-100">
                  {isDeleteModal ? 'Request permanent deletion?' : 'Deactivate your account?'}
                </h3>
                <p className="mt-1 text-sm text-slate-400">
                  {isDeleteModal
                    ? 'Your account is switched off straight away, then an administrator reviews your request and emails you. Nothing is deleted until they approve it.'
                    : 'You will be signed out and unable to sign back in until you reactivate. Nothing is deleted — your profile and data are kept.'}
                </p>
              </div>
            </div>

            <label className="mt-5 block">
              <span className="text-[11px] font-bold uppercase tracking-widest text-slate-500">
                Reason {isDeleteModal ? '(required)' : '(optional)'}
              </span>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                disabled={busy}
                rows={3}
                maxLength={REASON_LIMIT}
                placeholder={
                  isDeleteModal
                    ? 'Tell us why you want your account deleted'
                    : 'Why are you switching your account off?'
                }
                className={`mt-1.5 w-full resize-none rounded-xl border border-slate-800 bg-slate-950 px-3.5 py-2.5 text-sm text-slate-100 placeholder-slate-600 outline-none transition focus:border-rose-500/50 disabled:opacity-60 ${
                  isDeleteModal ? 'focus:border-rose-500/50' : 'focus:border-amber-500/50'
                }`}
              />
              <span className="mt-1 block text-right text-[11px] text-slate-600">
                {reason.trim().length}/{REASON_LIMIT}
              </span>
            </label>

            {error && (
              <p className="mt-3 rounded-xl border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-xs font-semibold text-rose-300">
                {error}
              </p>
            )}

            <div className="mt-6 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={closeModal}
                disabled={busy}
                className="rounded-xl px-4 py-2.5 text-xs font-black uppercase tracking-widest text-slate-400 transition hover:text-slate-200 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submit}
                disabled={busy || !canSubmit}
                className={`flex items-center gap-2 rounded-xl px-5 py-2.5 text-xs font-black uppercase tracking-widest text-white transition disabled:cursor-not-allowed disabled:opacity-50 ${
                  isDeleteModal ? 'bg-rose-500 hover:bg-rose-400' : 'bg-amber-500 hover:bg-amber-400'
                }`}
              >
                {busy ? (
                  <Loader2 size={15} className="animate-spin" />
                ) : isDeleteModal ? (
                  <Trash2 size={15} />
                ) : (
                  <LogOut size={15} />
                )}
                {busy
                  ? 'Working…'
                  : isDeleteModal
                  ? 'Send request'
                  : 'Deactivate'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountAccessPanel;
